/**
 * LinkedDocumentRail — net-new shared component (holistic plan §Net-new, S4.3).
 *
 * The one-record-many-documents chain: SO line ⇄ PO line ⇄ 裝櫃批次 ⇄ 收貨 ⇄
 * 發票, navigable from BOTH ends — every chip with an `onNavigate` is a
 * drill-through into its owning module (derive-never-restore: the rail links
 * to source documents, it never copies their data).
 *
 * Namespace-agnostic by design: all user-facing strings (stage labels, empty
 * text) arrive via the items so each consumer localizes from its own i18n
 * namespace. A stage with no documents renders its `emptyText` un-clickable —
 * absence is shown honestly, not hidden.
 */

import { Fragment } from 'react';
import type { KeyboardEvent } from 'react';
import { Typography, theme } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';

const { Text } = Typography;

export interface LinkedDocumentRailLink {
  /** Document reference shown on the chip, e.g. SO-202606-0001. */
  value: string;
  /** Present = navigable chip (drill into the owning module). */
  onNavigate?: () => void;
}

export interface LinkedDocumentRailItem {
  key: string;
  /** Localized stage label, e.g. 銷售訂單 / 裝櫃批次. */
  label: string;
  /** Documents at this stage (several POs, several invoices, …). */
  links: LinkedDocumentRailLink[];
  /** Localized text when the stage has no document yet. */
  emptyText: string;
}

export interface LinkedDocumentRailProps {
  items: LinkedDocumentRailItem[];
  /** Localized aria label for the rail as a whole. */
  ariaLabel?: string;
}

const LinkedDocumentRail = ({ items, ariaLabel }: LinkedDocumentRailProps) => {
  const { token } = theme.useToken();

  const chipBase = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: `0 ${token.paddingXS}px`,
    minHeight: token.sizeLG,
    borderRadius: token.borderRadiusSM,
    border: `1px solid ${token.colorBorderSecondary}`,
    backgroundColor: token.colorBgContainer,
    fontVariantNumeric: 'tabular-nums' as const,
  };

  const onChipKeyDown = (event: KeyboardEvent<HTMLSpanElement>, navigate: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate();
    }
  };

  return (
    <div
      role="navigation"
      aria-label={ariaLabel}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        columnGap: token.marginXS,
        rowGap: token.marginXS,
      }}
    >
      {items.map((item, index) => (
        <Fragment key={item.key}>
          {index > 0 && (
            <ArrowRightOutlined
              style={{
                color: token.colorTextTertiary,
                fontSize: token.fontSizeSM,
                marginTop: token.marginXS,
              }}
            />
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginXXS }}>
            <Text
              style={{
                fontSize: token.fontSizeSM,
                color: token.colorTextSecondary,
                whiteSpace: 'nowrap',
              }}
            >
              {item.label}
            </Text>
            {item.links.length === 0 ? (
              <span style={{ ...chipBase, color: token.colorTextDisabled }}>{item.emptyText}</span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginXXS }}>
                {item.links.map((link) =>
                  link.onNavigate ? (
                    <span
                      key={link.value}
                      role="button"
                      tabIndex={0}
                      onClick={link.onNavigate}
                      onKeyDown={(event) => onChipKeyDown(event, link.onNavigate!)}
                      style={{
                        ...chipBase,
                        color: token.colorPrimary,
                        borderColor: token.colorPrimaryBorder,
                        cursor: 'pointer',
                      }}
                    >
                      {link.value}
                    </span>
                  ) : (
                    <span key={link.value} style={{ ...chipBase, color: token.colorText }}>
                      {link.value}
                    </span>
                  )
                )}
              </div>
            )}
          </div>
        </Fragment>
      ))}
    </div>
  );
};

export default LinkedDocumentRail;
